import {
	faBat as Bat,
	faCat as Cat,
	faCow as Cow,
	faCrow as Crow,
	faDeer as Deer,
	faDog as Dog,
	faDove as Dove,
	faDuck as Duck,
	faElephant as Elephant,
	faFish as Fish,
	faFrog as Frog,
	faHorse as Horse,
	faKiwiBird as Kiwi,
	faMonkey as Monkey,
	faPig as Pig,
	faRabbit as Rabbit,
	faRam as Ram,
	faSheep as Sheep,
	faSnake as Snake,
	faSpider as Spider,
	faSquirrel as Squirrel,
	faTurtle as Turtle,
	faUnicorn as Unicorn,
	faWhale as Whale,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { getOpaqueFadedColor } from "./colors";

const ANONYMOUS_ICONS: { [key: string]: IconDefinition } = {
	Bat,
	Cat,
	Cow,
	Crow,
	Deer,
	Dog,
	Dove,
	Duck,
	Elephant,
	Fish,
	Frog,
	Horse,
	Kiwi,
	Monkey,
	Pig,
	Rabbit,
	Ram,
	Sheep,
	Snake,
	Spider,
	Squirrel,
	Turtle,
	Unicorn,
	Whale,
};

// Colors from our design library allocated to avatars.
// 200 - Flag Stem + Inactive Flag
// 600 - Active Ring + Active Flag
// 700 - Letter Backing + Tooltip backing
const UI_HEX_COLORS = [
	// [200, 600, 700]
	["FFCCCC", "D44243", "BA2728"],
	["FFE4BF", "F5834A", "DB662C"],
	["FFE9B3", "FCBF12", "E3A905"],
	["E0F7B5", "9ADD1C", "82C406"],
	["CEF5D9", "52CC75", "36B25A"],
	["C1F2E6", "00D5B6", "00BDA1"],
	["CCE9FF", "37A4FA", "1B8AE0"],
	["D1E1FB", "596FFF", "3950E5"],
	["D9D7FA", "8466FF", "6545E6"],
	["FFD1DD", "EA5278", "D1345B"],
] as const;

const colorId = (s: string): number => {
	return s
		.split("")
		.map(x => x.charCodeAt(0))
		.reduce((x, y) => x + y, 10);
};

export const str2ColorHex = (s: string): string => UI_HEX_COLORS[colorId(s) % UI_HEX_COLORS.length][1];

export const str2AttendeeColors = (s: string) => {
	const position = colorId(s) % UI_HEX_COLORS.length;

	const colors = {
		flag: UI_HEX_COLORS[position][0],
		ring: UI_HEX_COLORS[position][1],
		tooltip: UI_HEX_COLORS[position][2],
		get selection() {
			return getOpaqueFadedColor(UI_HEX_COLORS[position][1]);
		},
	};

	return colors;
};

/**
 * Generates a ui-avatar URL with better background colors
 */
export interface UIAvatarOptions {
	digits?: boolean;
	size?: number;
	background?: string;
	color?: string;
	rounded?: 0 | 1;
	uppercase?: 0 | 1;
	bold?: 0 | 1;
	format?: "png" | "svg";
	// This is intended to be used for icon avatars
	textForColor?: string;
}

export const uiAvatar = (
	value: string | number | IconDefinition,
	{
		size = 200,
		background = undefined,
		color = "FFFFFF",
		rounded = 0,
		uppercase = 1,
		bold = 1,
		format = "svg",
		textForColor = "",
	}: UIAvatarOptions = {},
): string => {
	let contents, fontSize, bg;
	if (typeof value === "number") {
		contents = value > 99 ? "99+" : `+${value}`;
		bg = background ?? str2ColorHex(contents);
		fontSize = 0.35;
	} else if (typeof value === "string") {
		contents = [...value][0] ?? "?"; // We have to spread the string due to emoji / unicode characters
		contents = uppercase ? contents.toUpperCase() : contents.toLowerCase();
		bg = background ?? str2ColorHex(contents);
		fontSize = 0.6;
	} else {
		// This enables us to use icons as values for Avatars
		// Uses FontAwesome's native viewBox dimensions with padding for proper centering
		const iconWidth = value.icon[0];
		const iconHeight = value.icon[1];
		const pathData = value.icon[4];

		// Add padding around the icon (25% on each side)
		const padding = Math.max(iconWidth, iconHeight) * 0.25;
		const viewBoxWidth = iconWidth + padding * 2;
		const viewBoxHeight = iconHeight + padding * 2;

		contents = `
		<svg
			aria-hidden="true"
			focusable="false"
			data-prefix="far"
			data-icon="${value.iconName}"
			role="img"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="${-padding} ${-padding} ${viewBoxWidth} ${viewBoxHeight}">
			${
				Array.isArray(pathData)
					? pathData.map(path => `<path fill="#${color}" d="${path}" />`).join("")
					: `<path fill="#${color}" d="${pathData}" />`
			}
		</svg>`;
		bg = background ?? str2ColorHex(textForColor);
		fontSize = 0.6;
	}
	if (format !== "svg") {
		return (
			`https://ui-avatars.com/api/${encodeURIComponent(contents)}` +
			`/${size}/${bg}/${color}/${contents.length}/${fontSize}/${rounded}/${uppercase}/${bold}/${format}/`
		);
	}

	// eslint-disable-next-line max-len
	const fontFamily = `system-ui, sans-serif`;

	const svg = `
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="${size}px"
			height="${size}px"
			viewBox="0 0 ${size} ${size}"
		>
			<${rounded ? "circle" : "rect"}
				fill="#${bg}"
				cx="${size / 2}"
				width="${size}"
				height="${size}"
				cy="${size / 2}"
				r="${size / 2}"
			/>
			${
				typeof value === "string" || typeof value === "number"
					? `<text
				x="50%"
				y="50%"
				style="color: #${color};line-height: 1;font-family: ${fontFamily}"
				alignment-baseline="middle"
				text-anchor="middle"
				font-size="${size * fontSize}"
				font-weight="${bold ? 600 : 400}"
				dy=".1em"
				dominant-baseline="middle"
				fill="#${color}"
			>
				${contents}
			</text>`
					: contents
			}
		</svg>
		`;
	return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

export function getAnonymousUserIcon(userName?: string): IconDefinition {
	if (!userName || !ANONYMOUS_ICONS[userName]) {
		return Duck;
	}
	return ANONYMOUS_ICONS[userName];
}
